import React, { useState } from 'react';
import { MOCK_USERS, MOCK_STATE_KEYS, MOCK_MEMBERSHIPS_MAP } from '../api/mockData';
import { Settings, Users, Building, HelpCircle, X, Check, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useCurrentUser } from '../contexts/AuthContext';
import { SHOULD_USE_MOCKS } from '../lib/clerk';

import { useTenant } from '../contexts/TenantContext';

export const DevToolbar: React.FC = () => {
  const { user: activeUser, loginAs } = useCurrentUser();
  const { activeBrandId: currentStyleId } = useTenant();
  const [isOpen, setIsOpen] = useState(false);
  const forceEnableDevtools = import.meta.env.VITE_ENABLE_DEVTOOLS === 'true';

  const [activeTab, setActiveTab] = useState<'users' | 'orgs'>(
    'users'
  );

  const userMemberships = activeUser ? (MOCK_MEMBERSHIPS_MAP[activeUser.id] || []) : [];

  const switchUser = (user: typeof MOCK_USERS[0]) => {
    loginAs({
      ...user,
      firstName: (user.name || '').split(' ')[0],
      lastName: (user.name || '').split(' ')[1] || '',
      fullName: user.name,
      primaryEmailAddress: { emailAddress: user.email },
      imageUrl: user.avatar,
      username: (user.name || '').toLowerCase().replace(' ', '')
    });
  };

  const switchContext = (orgSlug: string) => {
    localStorage.setItem('active_org', orgSlug);
    window.location.href = '/dashboard';
  };



  if (!SHOULD_USE_MOCKS && !forceEnableDevtools) return null;

  const showMockFeatures = SHOULD_USE_MOCKS;

  return (
    <div id="dev-toolbar" className="fixed bottom-4 right-4 z-[9999]">
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="w-12 h-12 bg-gray-900 text-white rounded-full flex items-center justify-center shadow-xl border border-white/10 hover:bg-gray-800 transition-colors"
        title="Development Mocks"
      >
        {isOpen ? <X className="w-6 h-6" /> : <Settings className="w-6 h-6" />}
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className="absolute bottom-16 right-0 w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden dark:bg-slate-900"
          >
            <div className="flex border-b border-gray-100">
              {showMockFeatures && (
                <>
                  <button 
                    onClick={() => setActiveTab('users')}
                    className={`flex-1 py-3 px-1 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 transition-colors ${activeTab === 'users' ? 'text-blue-600 bg-blue-50' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    <Users className="w-3.5 h-3.5" />
                    <span>Users</span>
                  </button>
                  <button 
                    onClick={() => setActiveTab('orgs')}
                    className={`flex-1 py-3 px-1 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 transition-colors ${activeTab === 'orgs' ? 'text-green-600 bg-green-50' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    <Building className="w-3.5 h-3.5" />
                    <span>Context</span>
                  </button>
                </>
              )}

            </div>

            <div className="max-h-[400px] overflow-y-auto scrollbar-hide p-2">
              {activeTab === 'users' ? (
                <div className="space-y-1">
                  <div className="flex items-center justify-between p-3 rounded-xl border border-rose-100 bg-rose-50/50 mb-2">
                    <div>
                      <p className="text-sm font-medium text-rose-900">God Mode</p>
                      <p className="text-[10px] text-rose-600">Impersonate SYSTEM_ADMIN everywhere</p>
                    </div>
                    <button
                      onClick={() => {
                        const isGodMode = localStorage.getItem('GOD_MODE_ENABLED') === 'true';
                        if (isGodMode) {
                          localStorage.removeItem('GOD_MODE_ENABLED');
                        } else {
                          localStorage.setItem('GOD_MODE_ENABLED', 'true');
                        }
                        window.location.reload();
                      }}
                      className={`w-10 h-6 rounded-full transition-colors relative ${localStorage.getItem('GOD_MODE_ENABLED') === 'true' ? 'bg-rose-500' : 'bg-gray-300'}`}
                    >
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${localStorage.getItem('GOD_MODE_ENABLED') === 'true' ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  {MOCK_USERS.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => switchUser(user)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                        activeUser?.id === user.id 
                          ? 'bg-blue-50 border border-blue-100 ring-1 ring-blue-100' 
                          : 'hover:bg-gray-50 border border-transparent dark:bg-slate-950'
                      }`}
                    >
                      <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full border border-gray-100 bg-gray-100 dark:bg-slate-800" />
                      <div className="text-left">
                        <p className={`text-sm font-medium ${activeUser?.id === user.id ? 'text-blue-900' : 'text-gray-900'}`}>
                          {user.name}
                        </p>
                        <p className="text-[10px] text-gray-500">{user.email}</p>
                      </div>
                      {activeUser?.id === user.id && <Check className="ml-auto w-4 h-4 text-blue-500" />}
                    </button>
                  ))}
                </div>
              ) : activeTab === 'orgs' ? (
                <div className="space-y-1">
                  {!activeUser && (
                    <div className="p-8 text-center text-gray-400 text-xs italic">
                      Please sign in to view hub contexts.
                    </div>
                  )}
                  {activeUser && (
                    <button
                      onClick={() => switchContext('global')}
                      className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 border border-transparent transition-all dark:bg-slate-950"
                    >
                      <div className="w-10 h-10 rounded-lg bg-gray-900 flex items-center justify-center text-white">
                        <Globe className="w-5 h-5" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Global Portal</p>
                        <p className="text-[10px] text-gray-500">Universal Dashboard View</p>
                      </div>
                    </button>
                  )}
                  {userMemberships.map((org) => (
                    <button
                      key={org.id}
                      onClick={() => switchContext(org.nodeSlug)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 border border-transparent transition-all dark:bg-slate-950"
                    >
                      <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center text-white text-xs font-bold">
                        {org.nodeName.substring(0, 2).toUpperCase()}
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{org.nodeName}</p>
                        <p className="text-[10px] text-gray-500 capitalize">{(org.role || '').toLowerCase()}</p>
                      </div>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between dark:bg-slate-950">
              {showMockFeatures ? (
                <div className="flex items-center gap-2 text-[10px] font-bold text-green-600">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                  MOCK MODE ACTIVE
                </div>
              ) : (
                <div className="flex items-center gap-2 text-[10px] font-bold text-purple-600">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                  BRANDING TOOLS
                </div>
              )}
              <p className="text-[10px] text-gray-400">v1.2.0-debug</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
